import { device } from 'utils/device';
import { OrganicLinesBasePhase } from 'app/views/home/webgl/particlesOptimised/departments/spaces/phases/OrganicLinesBasePhase';

/**
 * OrganicLinesPhase — FORK of SpacesDepartment._organicLines.
 *
 * VISUAL: the long formation dance — the swarm coalesces around five
 * anchors at the box centre, then the ComboCounter timeline walks the
 * anchors through corner/diamond/centre formations while the follow radius
 * tightens (2.5s: corners at quarter radius; 5s: a plus/diamond at fifth
 * radius; 7.5s: everything back to centre at full radius; 8.5s: radius 0 —
 * the swarm collapses ONTO the anchors; 9.5s: the follows release and the
 * whoozy drift carries the lines apart).
 */
export class OrganicLinesPhase extends OrganicLinesBasePhase {
	public readonly name = 'OrganicLines';

	public start(): void {
		const ctx = this._ctx;
		const core = ctx.core;
		const sizes = ctx.getFrameSizes();

		const followRadiusMax = 0.025;
		const mobileScaleBumpMax = 0.05;
		const r = 1 - Math.min(1, device.innerWidth / 1000);

		for (let n = this._totalTargets; n < ctx.activeCount; n++) {
			const lane = ctx.range.start + n;
			this.organicLineStartLane(lane);
			this.followLane(lane, this.rollFollowTarget(), followRadiusMax + r * mobileScaleBumpMax);
			core.blendAdd[lane] = 1;
		}

		for (let t = 0; t < this._totalTargets; t++) {
			this.organicTargetLane(ctx.range.start + t, sizes.xc, sizes.yc);
		}

		const xq = sizes.width / 4;
		const hq = sizes.height / 4;
		const setTarget = (t: number, x: number, y: number) => {
			core.relX[ctx.range.start + t] = x;
			core.relY[ctx.range.start + t] = y;
		};

		this._comboCounter.add(() => {
			setTarget(0, sizes.xc + xq / 1, sizes.yc + hq / 1);
			setTarget(1, sizes.xc + xq / 1, sizes.yc - hq / 1);
			setTarget(2, sizes.xc - xq / 1, sizes.yc - hq / 1);
			setTarget(3, sizes.xc - xq / 1, sizes.yc + hq / 1);
			setTarget(4, sizes.xc - xq / 1, sizes.yc + hq / 1);

			this.setSwarmRadius(followRadiusMax / 4 + r * (mobileScaleBumpMax / 4));
		}, 2500);

		this._comboCounter.add(() => {
			setTarget(0, sizes.xc, sizes.yc);
			setTarget(1, sizes.xc - xq / 1, sizes.yc);
			setTarget(2, sizes.xc, sizes.yc + hq / 1);
			setTarget(3, sizes.xc + xq / 1, sizes.yc);
			setTarget(4, sizes.xc, sizes.yc - hq / 1);

			this.setSwarmRadius(followRadiusMax / 5 + r * (mobileScaleBumpMax / 5));
		}, 5000);

		this._comboCounter.add(() => {
			setTarget(0, sizes.xc, sizes.yc);
			setTarget(1, sizes.xc, sizes.yc);
			setTarget(2, sizes.xc, sizes.yc);
			setTarget(3, sizes.xc, sizes.yc);
			setTarget(4, sizes.xc, sizes.yc);

			this.setSwarmRadius(followRadiusMax + r * mobileScaleBumpMax);
		}, 7500);

		this._comboCounter.add(() => {
			this.setSwarmRadius(0);
		}, 8500);

		this._comboCounter.add(() => {
			for (let n = this._totalTargets; n < ctx.activeCount; n++) {
				core.followIdx[ctx.range.start + n] = -1;
			}
		}, 9500);
	}
}
